'use client';

import { getFragmentData } from '@packages/gql/generated/shopping';
import { ProductRecommendationFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductRecommendationFragmentFragmentDoc';
import { SearchReplaceDocument } from '@packages/gql/generated/shopping/SearchReplaceDocument';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoProductCard } from '../../RecoProductCard';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type SearchReplaceProductsRecoSliderProps = ProductsRecoSliderProps &
  Pick<RequestParams, 'productId' | 'size'>;

/* GraphQL */ `
  query SearchReplace($productId: String!, $locale: String!, $size: String) {
    searchReplace(productId: $productId, locale: $locale, size: $size) {
      title
      type
      recommendations {
        ...ProductRecommendationFragment
      }
    }
  }
`;

const RDE_TYPE: RdeType = 'searchReplace';

/**
 * SearchReplaceProductsRecoSlider component description displayed in storybook
 * */
export const SearchReplaceProductsRecoSlider: FC<SearchReplaceProductsRecoSliderProps> = ({
  backgroundColor,
  elementIndex = 0,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  pageTemplate,
  preferTitle = false,
  title,
  productId,
  size = '',
}) => {
  const { locale } = useIntl();
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: SearchReplaceDocument,
    variables: {
      locale,
      productId,
      size,
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.searchReplace.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) =>
          getFragmentData(ProductRecommendationFragmentFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.searchReplace.type;

  useProductsRecoTracking({
    elementIndex,
    pageTemplate,
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title,
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <RecoCarousel
      title={preferTitle ? title : data?.searchReplace.title || title}
      loadingState={!filteredRecommendations}
      backgroundColor={backgroundColor}
      ref={recoEl}
    >
      {filteredRecommendations?.map((recommendation, idx) => (
        <RecoProductCard
          pageTemplate={pageTemplate}
          // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          elementIndex={elementIndex}
          position={idx}
          recommendationData={recommendation}
          productsLength={filteredRecommendations.length}
          rdeType={RDE_TYPE}
          onClick={(akl, sku) => {
            onProductClick?.(akl, sku);
          }}
        />
      ))}
    </RecoCarousel>
  );
};
